import { api } from '@/lib/api'

export type NumberType = 'local' | 'tollfree'

export interface AvailableNumber {
  phoneNumber: string
  friendlyName?: string
  /** Readable city name (Montréal, Laval), null when the provider does not know it. */
  locality?: string | null
  region?: string | null
  isoCountry?: string
}

/** The place a search was made for: a city of the catalogue, or an area code. */
export interface LieuRecherche {
  id?: string
  nom?: string
  province?: string
  indicatif?: string
  indicatifs: string[]
}

export interface NumbersSearchResult {
  numbers: AvailableNumber[]
  lieu: LieuRecherche | null
  /** The area code asked for has nothing left: these are its neighbours (514 -> 438). */
  repli: { demande: string; servis: string[] } | null
  /** Nothing left in the whole territory: these are the closest numbers to this city. */
  proches: string | null
}

interface AvailableNumbersResponse extends Partial<Omit<NumbersSearchResult, 'numbers'>> {
  ok?: boolean
  data?: AvailableNumber[]
  message?: string
}

export interface SearchNumbersInput {
  type: NumberType
  country?: string
  limit?: number
  /** A city id of the catalogue (qc-dorval) or a 3-digit area code. */
  lieu?: string
  /** Asks the server for a fresh lot instead of the one it keeps for two minutes. */
  nouveau?: boolean
}

/** GET /api/numbers/available/ */
export async function searchNumbers(input: SearchNumbersInput): Promise<NumbersSearchResult> {
  const country = input.country || 'CA'
  const params = new URLSearchParams({
    country,
    type: input.type,
    limit: String(input.limit ?? 6),
  })
  if (input.lieu && input.type === 'local') {
    // Canada and the United States: all the area codes of the place, around it. Elsewhere: the area code alone.
    if (country === 'CA' || country === 'US') params.set('lieu', input.lieu)
    else if (/^\d{3}$/.test(input.lieu)) params.set('areaCode', input.lieu)
  }
  if (input.nouveau) params.set('nouveau', '1')

  const { data } = await api.get<AvailableNumbersResponse>(`/api/numbers/available/?${params.toString()}`)
  return {
    numbers: data.ok && Array.isArray(data.data) ? data.data.filter((n) => n.phoneNumber) : [],
    lieu: data.lieu ?? null,
    repli: data.repli ?? null,
    proches: data.proches ?? null,
  }
}

export interface IndicatifCatalogue {
  code: string
  /** Province, or state in the United States. */
  province: string
  pays: 'CA' | 'US'
  /** Main cities of the code, biggest first. */
  villes: string[]
  /** Codes of the same territory (514 -> 438, 263). */
  voisins: string[]
}

export interface VilleCatalogue {
  id: string
  nom: string
  province: string
  pays: 'CA' | 'US'
  indicatifs: string[]
  autres_noms: string[]
}

export interface LieuxCatalogue {
  indicatifs: IndicatifCatalogue[]
  villes: VilleCatalogue[]
}

/** GET /api/numbers/lieux/ : Canadian and US cities and area codes, loaded once to suggest while typing. */
export async function fetchLieux(): Promise<LieuxCatalogue> {
  const { data } = await api.get<LieuxCatalogue>('/api/numbers/lieux/')
  return data
}

/** GET /api/numbers/lieu-de-depart/ : the catalogue city nearest to the connection, or null (abroad, VPN). */
export async function fetchLieuDeDepart(): Promise<string | null> {
  const { data } = await api.get<{ lieu: string | null }>('/api/numbers/lieu-de-depart/')
  return data.lieu
}

export interface SelectNumberInput {
  phone_number: string
  wants_port: boolean
  port_number: string
  is_toll_free: boolean
}

/** PUT /api/onboarding/select-number/ */
export async function selectOnboardingNumber(input: SelectNumberInput): Promise<void> {
  await api.put('/api/onboarding/select-number/', input)
}

export interface CompanyInfoInput {
  name: string
  timezone: string
  company_size?: string
  industry?: string
  website?: string | null
  /** Comment l'entreprise a connu Callizy, dit par elle à l'étape 2. */
  discovery_source?: string
  /** Qui, où, lequel : la précision des choix qui en demandent une. */
  discovery_detail?: string
  /** Partner code typed at step 2; ignored by the server when unknown or when the company is already tied. */
  partner_code?: string
}

/** PUT /api/onboarding/company-info/ */
export async function updateCompanyInfo(input: CompanyInfoInput): Promise<void> {
  await api.put('/api/onboarding/company-info/', input)
}

export interface CompanyAddressInput {
  address1: string
  address2?: string
  city: string
  province: string
  postal: string
  country?: string
}

/** PUT /api/onboarding/address/ */
export async function updateCompanyAddress(input: CompanyAddressInput): Promise<void> {
  await api.put('/api/onboarding/address/', input)
}

export interface FinalizeOnboardingResponse {
  ok: boolean
  phone_number: string | null
  number_unavailable: boolean
  /** Area code of the number that could not be bought, to resume the search there. */
  indicatif?: string | null
  message: string
  port_requested: boolean
}

/** POST /api/onboarding/finalize/ — achète le numéro Twilio post-paiement. */
export async function finalizeOnboarding(devSkip = false): Promise<FinalizeOnboardingResponse> {
  const url = devSkip ? '/api/onboarding/finalize/?dev_skip=true' : '/api/onboarding/finalize/'
  const { data } = await api.post<FinalizeOnboardingResponse>(url)
  return data
}

// ---------------------------------------------------------------------------
// Mise en service après le paiement
// ---------------------------------------------------------------------------

export type EcranMiseEnService = 'langue' | 'reponse' | 'options' | 'urgence'

export interface EtatMiseEnService {
  /** Dernier écran enregistré, vide si aucun. */
  etape: '' | EcranMiseEnService
  terminee: boolean
  langue: '' | 'fr' | 'en'
  reponse: '' | 'ia' | 'messagerie'
  options: { enregistrement: boolean; resume: boolean; transcription: boolean } | null
  urgence: {
    address1: string
    address2: string
    city: string
    province: string
    postal: string
    /** declaree, a_corriger (refusée par Twilio, le client n'est pas bloqué), sans_objet. */
    statut: '' | 'en_cours' | 'declaree' | 'a_corriger' | 'sans_objet'
    avis_lu: boolean
  } | null
}

/** GET /api/onboarding/mise-en-service/ : pour reprendre là où le client s'était arrêté. */
export async function fetchMiseEnService(): Promise<EtatMiseEnService> {
  const { data } = await api.get<EtatMiseEnService>('/api/onboarding/mise-en-service/')
  return data
}

/** PUT /api/onboarding/mise-en-service/<écran>/ : enregistre et applique un écran. */
export async function enregistrerEcran(ecran: EcranMiseEnService, donnees: Record<string, unknown>): Promise<EtatMiseEnService> {
  const { data } = await api.put<EtatMiseEnService>(`/api/onboarding/mise-en-service/${ecran}/`, donnees)
  return data
}
